/**
 * Admin Dashboard entry point.
 * Initializes Firebase, QueryClient, and renders the app with providers.
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import { useAdminAuthStore } from './stores/useAdminAuthStore';

import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import './styles/globals.css';


// ============================================================
// Query Client
// ============================================================

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30 * 1000, // admin data changes often (orders, stock)
            retry: 1,
            refetchOnWindowFocus: false,
        },
        mutations: {
            retry: 0,
        },
    },
});

// ============================================================
// Auth Initializer
// ============================================================

function AuthInitializer({ children }: { children: React.ReactNode }) {
    const initialize = useAdminAuthStore((s) => s.initialize);

    useEffect(() => {
        const unsubscribe = initialize();
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [initialize]);

    return <>{children}</>;
}

// ============================================================
// Render
// ============================================================

// A data router (instead of <BrowserRouter>) enables useBlocker for unsaved-change
// guards. The catch-all route keeps all routing inside <App /> unchanged.
const router = createBrowserRouter([
    {
        path: '*',
        element: (
            <AuthInitializer>
                <ErrorBoundary>
                    <App />
                </ErrorBoundary>
            </AuthInitializer>
        ),
    },
]);

const root = document.getElementById('root');

if (!root) {
    throw new Error('Root element not found');
}

ReactDOM.createRoot(root).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            <Toaster
                position="top-right"
                richColors
                closeButton
                toastOptions={{
                    duration: 4000,
                    style: { borderRadius: '10px', fontSize: '14px' },
                }}
            />
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    </React.StrictMode>,
);
